import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MegaMenuItem } from "./MegaMenu";

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  ctaLabel: string;
  onCtaClick: () => void;
  /**
   * Optional interceptor for menu item clicks. Return `true` if the click was
   * handled (e.g. opened a modal) and the default Link navigation should be
   * suppressed. Return `false` (or omit) to let the Link navigate normally.
   */
  onItemNavigate?: (to: string) => boolean;
  groups: Array<{
    label: string;
    items: MegaMenuItem[];
  }>;
}

/**
 * Full-screen mobile nav overlay. Accordion sections, target-language toggle,
 * primary CTA pinned at the bottom.
 */
export const MobileNav = ({ open, onClose, ctaLabel, onCtaClick, onItemNavigate, groups }: MobileNavProps) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

  // Lock body scroll while open + Escape closes.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  return (
    <div
      className={`fixed inset-0 z-[120] md:hidden transition-all duration-300 ${
        open ? "visible" : "invisible pointer-events-none"
      }`}
      aria-hidden={!open}
    >
      <div
        className={`absolute inset-0 bg-[hsl(var(--bg-canvas))]/95 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        className={`relative h-full w-full bg-[hsl(var(--bg-canvas))]
          flex flex-col transform transition-transform duration-300
          ${open ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--border-hairline))]">
          <span
            className="font-display font-black text-[hsl(var(--fg-cream))]"
            style={{ fontSize: 22, letterSpacing: "-0.03em" }}
          >
            BRICK<span className="text-[hsl(var(--accent-brick))]">.</span>
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close menu"
            className="text-[hsl(var(--fg-cream))] hover:bg-white/[0.04]"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Scrollable groups */}
        <nav className="flex-1 overflow-y-auto px-6 py-6">
          <ul className="space-y-1">
            {groups.map((group, idx) => {
              const expanded = expandedIndex === idx;
              return (
                <li key={group.label} className="border-b border-[hsl(var(--border-hairline))] last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setExpandedIndex(expanded ? null : idx)}
                    aria-expanded={expanded}
                    className="w-full flex items-center justify-between py-4
                      text-left text-[hsl(var(--fg-cream))]
                      focus:outline-none focus-visible:ring-2
                      focus-visible:ring-[hsl(var(--accent-brick))]
                      focus-visible:rounded-sm"
                  >
                    <span
                      className="text-[11px] font-mono uppercase tracking-[0.1em] text-[hsl(var(--fg-muted))]"
                    >
                      {group.label}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-[hsl(var(--fg-muted))] transition-transform ${
                        expanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {expanded && (
                    <ul className="pb-4 space-y-1">
                      {group.items.map((item) => (
                        <li key={item.to}>
                          <Link
                            to={item.to}
                            onClick={(e) => {
                              if (onItemNavigate?.(item.to)) {
                                e.preventDefault();
                              }
                              onClose();
                            }}
                            className="block py-2 no-underline font-display font-extrabold tracking-[-0.02em]
                              text-[hsl(var(--fg-cream))]/85 hover:text-[hsl(var(--fg-cream))]
                              transition-colors"
                            style={{ fontSize: 22, lineHeight: 1.2 }}
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer: CTA */}
        <div className="px-6 py-5 border-t border-[hsl(var(--border-hairline))] space-y-3">
          <button
            onClick={() => {
              onCtaClick();
              onClose();
            }}
            className="w-full py-3 text-[13px] font-medium
              bg-[hsl(var(--accent-brick))] text-[hsl(var(--fg-cream))]
              hover:bg-[hsl(var(--accent-brick-hover))]
              transition-colors duration-150 ease-out
              rounded-sm
              focus:outline-none focus-visible:ring-2
              focus-visible:ring-[hsl(var(--fg-cream))]
              focus-visible:ring-offset-2
              focus-visible:ring-offset-[hsl(var(--bg-canvas))]"
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
